import React, { memo, useCallback, useRef } from 'react';
import { KeyboardGrid, KeyButton, EmptyCell, ArrowIcon } from './NumericKeyboard.styles';
import { hapticFeedback } from '@lib/telegram';

// Types
interface NumericKeyboardProps {
  onChange: (value: string | 'delete') => void;
  emptySpaceButton?: 'none' | 'period' | 'thousand' | 'comma';
}


// Main Component - Using memo for performance optimization
const NumericKeyboard: React.FC<NumericKeyboardProps> = memo(({
  onChange,
  emptySpaceButton = 'none'
}) => {
  // Add debounce mechanism to prevent rapid multiple button presses
  const lastPressTimeRef = useRef<{[key: string]: number}>({});
  const debounceTimeMs = 20; // Minimum time between key presses in milliseconds

  // Event handlers
  const handleKeyPress = useCallback((key: string) => {
    hapticFeedback('light');
    const now = Date.now();
    const lastPressTime = lastPressTimeRef.current[key] || 0;
    
    // Only process the key press if enough time has passed since the last press
    if (now - lastPressTime > debounceTimeMs) {
      onChange(key);
      lastPressTimeRef.current[key] = now;
    }
  }, [onChange]);

  const handleDeletePress = useCallback(() => {
    hapticFeedback('light');
    const now = Date.now();
    const lastPressTime = lastPressTimeRef.current['delete'] || 0;
    
    // Only process the delete press if enough time has passed since the last press
    if (now - lastPressTime > debounceTimeMs) {
      onChange('delete');
      lastPressTimeRef.current['delete'] = now;
    }
  }, [onChange]);

  const handlePeriodPress = useCallback(() => {
    hapticFeedback('light');
    const now = Date.now();
    const lastPressTime = lastPressTimeRef.current['.'] || 0;
    
    if (now - lastPressTime > debounceTimeMs) {
      onChange('.');
      lastPressTimeRef.current['.'] = now;
    }
  }, [onChange]);

  const handleCommaPress = useCallback(() => {
    hapticFeedback('light');
    const now = Date.now();
    const lastPressTime = lastPressTimeRef.current[','] || 0;
    
    if (now - lastPressTime > debounceTimeMs) {
      onChange(',');
      lastPressTimeRef.current[','] = now;
    }
  }, [onChange]);

  const handleThousandPress = useCallback(() => {
    hapticFeedback('light');
    const now = Date.now();
    const lastPressTime = lastPressTimeRef.current['000'] || 0;
    
    if (now - lastPressTime > debounceTimeMs) {
      onChange('000');
      lastPressTimeRef.current['000'] = now;
    }
  }, [onChange]);

  // Pre-define buttons for performance - avoid recreating on every render
  const buttons = [
    { key: '1', value: '1' },
    { key: '2', value: '2' },
    { key: '3', value: '3' },
    { key: '4', value: '4' },
    { key: '5', value: '5' },
    { key: '6', value: '6' },
    { key: '7', value: '7' },
    { key: '8', value: '8' },
    { key: '9', value: '9' },
    { 
      key: 'special', 
      value: emptySpaceButton === 'period' ? '.' : 
             emptySpaceButton === 'comma' ? ',' : 
             emptySpaceButton === 'thousand' ? '000' : '',
      isSpecial: emptySpaceButton !== 'none',
      isHidden: emptySpaceButton === 'none'
    },
    { key: '0', value: '0' },
    { key: 'delete', value: 'delete', isAction: true },
  ];

  return (
    <KeyboardGrid role="group" aria-label="Numeric Keyboard">
      {buttons.map((button) => {
        if (button.isHidden) {
          return <EmptyCell key={button.key} />;
        } else if (button.isAction) {
          return (
            <KeyButton
              key={button.key}
              onClick={handleDeletePress}
              aria-label="Delete"
            >
              <ArrowIcon />
            </KeyButton>
          );
        } else if (button.isSpecial) {
          if (emptySpaceButton === 'period') {
            return (
              <KeyButton
                key={button.key}
                onClick={handlePeriodPress}
                aria-label="Period"
              >
                .
              </KeyButton>
            );
          } else if (emptySpaceButton === 'comma') {
            return (
              <KeyButton
                key={button.key}
                onClick={handleCommaPress}
                aria-label="Comma"
              >
                ,
              </KeyButton>
            );
          } else if (emptySpaceButton === 'thousand') {
            return (
              <KeyButton
                key={button.key}
                onClick={handleThousandPress}
                aria-label="Thousand"
              >
                000
              </KeyButton>
            );
          }
        }
        return (
          <KeyButton
            key={button.key}
            onClick={() => handleKeyPress(button.value)}
            aria-label={button.value}
          >
            {button.value}
          </KeyButton>
        );
      })}
    </KeyboardGrid>
  );
});

// DisplayName for debugging
NumericKeyboard.displayName = 'NumericKeyboard';

export default NumericKeyboard;